import { useEffect, useState } from "react";
import { ownArticleNumber } from "@/lib/catalog";

/** Eén zoekbare sub-variant (kleur/afwerking/lengte) uit data/seed/variant-index.json.
 *  De basis-catalogus toont alleen het hoofdartikel; deze index maakt élke variant
 *  vindbaar op naam én artikelnummer, met een deep-link naar de productpagina (?variant=). */
export interface VariantEntry {
  slug: string;
  base: string;
  /** Positie in de variant-lijst → volgnummer voor het GT-nummer (GT-…-N). */
  pos: number;
  name: string;
  articleNumber: string;
  priceCents: number;
  label: string;
  image: string;
  category: string;
  merk: string;
  groep: string;
}

let cache: VariantEntry[] | null = null;
let loading: Promise<VariantEntry[]> | null = null;

/** Lazy: laadt de (grote) variant-index als aparte chunk, pas wanneer er gezocht wordt. */
export function loadVariantIndex(): Promise<VariantEntry[]> {
  if (cache) return Promise.resolve(cache);
  if (!loading) {
    loading = import("../../data/seed/variant-index.json").then((m) => {
      cache = m.default as unknown as VariantEntry[];
      return cache;
    });
  }
  return loading;
}

/** Hook: geeft de variant-index terug (leeg tot geladen). `enabled` triggert het laden. */
export function useVariantIndex(enabled: boolean): VariantEntry[] {
  const [items, setItems] = useState<VariantEntry[]>(cache ?? []);
  useEffect(() => {
    if (!enabled || cache) return;
    let alive = true;
    loadVariantIndex().then((v) => {
      if (alive) setItems(v);
    });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return cache ?? items;
}

/** Klant-GT-nummer van een variant: basis-GT + volgnummer (identiek aan de productpagina). */
export function variantGtNumber(v: VariantEntry): string {
  const baseGt = ownArticleNumber(v.base, "");
  return baseGt ? `${baseGt}-${v.pos}` : v.articleNumber || "";
}

/** Deep-link naar de productpagina met de variant voorgeselecteerd. */
export function variantHref(v: VariantEntry): string {
  return `/artikel/${v.base}?variant=${v.slug}`;
}
